import PropTypes from 'prop-types'

const isDev = import.meta.env.DEV

/**
 * Last-resort, theme-independent fallback for the top-level boundaries in
 * `index.jsx` / `App.jsx`. Those boundaries sit OUTSIDE `ThemeProvider`, so
 * this component must not depend on MUI — it uses plain inline styles only.
 *
 * Used when a provider or the layout shell itself crashes (the in-layout
 * boundaries catch everything below them first). Offers a hard page reload
 * since the React tree above the failure may be unusable.
 *
 * Receives `react-error-boundary` props: { error }.
 */
const RootErrorFallback = ({ error }) => (
    <div
        role="alert"
        style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 24,
            textAlign: 'center',
            backgroundColor: '#f7f8fa',
            color: '#1c2025',
            fontFamily:
                'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        }}
    >
        <h1 style={{ fontSize: 22, margin: 0 }}>Something went wrong</h1>
        <p style={{ maxWidth: 480, margin: 0, color: '#5a6573' }}>
            The application ran into an unexpected problem. Reloading the page
            usually fixes it.
        </p>
        {isDev && error?.message && (
            <pre
                style={{
                    maxWidth: '90vw',
                    overflow: 'auto',
                    textAlign: 'left',
                    fontSize: 12,
                    padding: 16,
                    borderRadius: 8,
                    backgroundColor: '#fdecea',
                    color: '#b71c1c'
                }}
            >
                {error.message}
                {error.stack ? `\n\n${error.stack}` : ''}
            </pre>
        )}
        <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
                cursor: 'pointer',
                border: 'none',
                borderRadius: 8,
                padding: '10px 20px',
                fontSize: 15,
                color: '#fff',
                backgroundColor: '#2563eb'
            }}
        >
            Reload page
        </button>
    </div>
)

RootErrorFallback.propTypes = {
    error: PropTypes.object
}

export default RootErrorFallback
